import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Badge, Input, Select, TextArea, Modal, StatCard, Table } from '../components/Common';
import SowWizard from '../components/SowWizard';

export default function Billing() {
  const { apiFetch } = useAuth();
  const [engagements, setEngagements] = useState([]);
  const [summary, setSummary] = useState({});
  const [filter, setFilter] = useState('all');
  const [engClientFilter, setEngClientFilter] = useState('');
  const [engNameFilter, setEngNameFilter] = useState('');
  const [showBillModal, setShowBillModal] = useState(false);
  const [billForm, setBillForm] = useState({});
  const [activeTab, setActiveTab] = useState('engagements');

  // SoW state
  const [sows, setSows] = useState([]);
  const [clients, setClients] = useState([]);
  const [showSowModal, setShowSowModal] = useState(false);
  const [sowForm, setSowForm] = useState({});
  const [editingSow, setEditingSow] = useState(null);
  const [sowFilter, setSowFilter] = useState('');
  const [clientSows, setClientSows] = useState([]);
  const [saving, setSaving] = useState({});
  const [sowClientFilter, setSowClientFilter] = useState('');
  const [sowFile, setSowFile] = useState(null);
  const [showSowWizard, setShowSowWizard] = useState(false);

  const load = useCallback(async () => {
    const [engRes, sumRes, sowRes, cliRes] = await Promise.all([
      apiFetch('/billing'), apiFetch('/billing/summary'),
      apiFetch('/billing/sows'), apiFetch('/clients')
    ]);
    setEngagements(await engRes.json());
    setSummary(await sumRes.json());
    setSows(await sowRes.json());
    setClients(await cliRes.json());
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  // Inline SoW assignment — fires on dropdown change or qty blur
  const assignSow = async (engId, sowId, sowCredits) => {
    setSaving(s => ({ ...s, [engId]: true }));
    try {
      const res = await apiFetch(`/billing/${engId}/assign-sow`, {
        method: 'PUT',
        body: JSON.stringify({ sowId: sowId || null, sowCredits: parseFloat(sowCredits) || 1 })
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err.error || 'Failed to assign SoW');
      }
      await load();
    } catch { alert('Failed to assign SoW'); }
    setSaving(s => ({ ...s, [engId]: false }));
  };

  const markBilled = async (engId) => {
    const res = await apiFetch(`/billing/${engId}/bill`, {
      method: 'PUT', body: JSON.stringify({ isBilled: true })
    });
    if (res.ok) load();
    else { const err = await res.json(); alert(err.error || 'Failed to mark as billed'); }
  };

  // Billing modal — save with SoW assignment
  const saveBilling = async () => {
    const res = await apiFetch(`/billing/${billForm.id}/bill`, {
      method: 'PUT',
      body: JSON.stringify({
        billingAmount: parseFloat(billForm.billingAmount) || 0,
        isBilled: billForm.isBilled,
        serviceOrder: billForm.serviceOrder || null,
        sowId: billForm.sowId || null,
        // parseFloat, not parseInt: sow_credits is numeric(4,1) and the
        // Credits dropdown offers halves. parseInt turned 1.5 into 1 on any
        // save, and 0.5 into 1 — doubling it — even when nothing was edited.
        sowCredits: parseFloat(billForm.sowCredits) || 1
      })
    });
    // The response was discarded, so a refusal — over-committed credits, a
    // SoW belonging to another client, a scope denial — closed the modal and
    // threw away what was typed, looking exactly like a save. markBilled
    // already reports its failures; this now does too.
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      alert(err.error || 'Failed to save billing');
      return;
    }
    setShowBillModal(false);
    setBillForm({});
    setClientSows([]);
    load();
  };

  // Open billing modal — fetch client SoWs
  const openBillModal = async (row) => {
    setBillForm({
      id: row.id,
      billingAmount: row.billing_amount,
      isBilled: row.is_billed,
      serviceOrder: row.service_order || '',
      sowId: row.sow_id || '',
      sowCredits: parseFloat(row.sow_credits) || 1
    });
    if (row.client_id) {
      try {
        // includeId so a SoW that has since been completed still comes back;
        // without it the select has no option matching billForm.sowId and
        // renders as "— No SoW —" for an engagement that has one.
        const res = await apiFetch(`/billing/sows/by-client/${row.client_id}`
          + (row.sow_id ? `?includeId=${encodeURIComponent(row.sow_id)}` : ''));
        setClientSows(await res.json());
      } catch { setClientSows([]); }
    }
    setShowBillModal(true);
  };

  // SoW CRUD
  const saveSow = async () => {
    const method = editingSow ? 'PUT' : 'POST';
    const url = editingSow ? `/billing/sows/${editingSow}` : '/billing/sows';
    const res = await apiFetch(url, {
      method,
      body: JSON.stringify({
        clientId: sowForm.clientId,
        sowNumber: sowForm.sowNumber,
        purchaseOrder: sowForm.purchaseOrder || null,
        serviceOrder: sowForm.serviceOrder || null,
        description: sowForm.description || null,
        totalCredits: parseInt(sowForm.totalCredits) || 1,
        billingAmount: parseFloat(sowForm.billingAmount) || 0,
        startDate: sowForm.startDate || null,
        endDate: sowForm.endDate || null,
        status: sowForm.status || 'Active',
        notes: sowForm.notes || null
      })
    });
    if (!res.ok) {
      const err = await res.json();
      alert(err.error || 'Failed to save SoW');
      return;
    }
    const saved = await res.json();
    const sowId = editingSow || saved.id;

    if (sowFile && sowId) {
      const fd = new FormData();
      fd.append('file', sowFile);
      await apiFetch(`/billing/sows/${sowId}/file`, { method: 'POST', body: fd, isFormData: true });
    }

    setShowSowModal(false);
    setSowForm({});
    setSowFile(null);
    setEditingSow(null);
    load();
  };

  const deleteSow = async (id) => {
    if (!window.confirm('Delete this SoW? This cannot be undone.')) return;
    const res = await apiFetch(`/billing/sows/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      const err = await res.json();
      alert(err.error || 'Failed to delete SoW');
      return;
    }
    load();
  };

  const openEditSow = (sow) => {
    setEditingSow(sow.id);
    setSowForm({
      clientId: sow.client_id,
      sowNumber: sow.sow_number,
      purchaseOrder: sow.purchase_order || '',
      serviceOrder: sow.service_order || '',
      description: sow.description || '',
      totalCredits: sow.total_credits,
      billingAmount: sow.billing_amount || '',
      startDate: sow.start_date ? sow.start_date.split('T')[0] : '',
      endDate: sow.end_date ? sow.end_date.split('T')[0] : '',
      status: sow.status,
      notes: sow.notes || '',
      existingFile: sow.original_name || null,
      fileSize: sow.file_size || null
    });
    setSowFile(null);
    setShowSowModal(true);
  };

  const openNewSow = () => {
    setEditingSow(null);
    setSowForm({ status: 'Active', totalCredits: 1 });
    setSowFile(null);
    setShowSowModal(true);
  };

  const removeSowFile = async (sowId) => {
    if (!window.confirm('Remove the uploaded SoW document?')) return;
    const res = await apiFetch(`/billing/sows/${sowId}/file`, { method: 'DELETE' });
    if (res.ok) {
      setSowForm(f => ({ ...f, existingFile: null, fileSize: null }));
      load();
    } else {
      alert('Failed to remove file');
    }
  };

  const downloadSowFile = async (sowId, fileName) => {
    const res = await apiFetch(`/billing/sows/${sowId}/file`);
    if (!res.ok) { alert('Failed to download file'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Filtered engagements
  const filtered = engagements.filter(e => {
    if (filter === 'unbilled') return e.status === 'Complete' && !e.is_billed && e.status !== 'Cancelled';
    if (filter === 'billed') return e.is_billed;
    if (filter === 'cancelled') return e.status === 'Cancelled';
    if (filter === 'no_sow') return !e.sow_id;
    return true;
  }).filter(e => {
    if (!sowFilter) return true;
    if (sowFilter === '__none') return !e.sow_id;
    return e.sow_id === sowFilter;
  }).filter(e => {
    if (!engClientFilter) return true;
    return e.client_id === engClientFilter;
  }).filter(e => {
    if (!engNameFilter) return true;
    const q = engNameFilter.toLowerCase();
    return (e.name || '').toLowerCase().includes(q) || (e.engagement_type || '').toLowerCase().includes(q);
  });

  // Export what is on screen, in the column order that was asked for.
  //
  // `filtered` IS the current view: the status chip, the SoW filter, the client
  // filter and the name search have all been applied to it, so an export taken
  // while looking at Unbilled contains the unbilled rows and nothing else.
  //
  // Amount goes out as a bare number rather than "$7,500.00" -- a spreadsheet
  // can sum 7500.00 and cannot sum a currency string. An inherited value is
  // exported as the value: the italic on screen means "not copied onto the
  // engagement yet", which is a fact about this application rather than about
  // the invoice.
  const exportCsv = () => {
    const cell = (v) => {
      const str = v === null || v === undefined ? '' : String(v);
      return /[",\n]/.test(str) ? '"' + str.replace(/"/g, '""') + '"' : str;
    };
    const body = filtered.map(e => {
      const amt = amountFor(e);
      return [
        amt.value === null ? '' : amt.value.toFixed(2),
        e.name || '',
        serviceOrderFor(e).value || '',
        qtyFor(e),
      ].map(cell).join(',');
    });
    const csv = ['Amount,Name,Service Order,Quantity'].concat(body).join('\r\n') + '\r\n';

    // The BOM is so Excel reads it as UTF-8 rather than the local code page.
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'billing-' + (filter === 'no_sow' ? 'no-sow' : filter) + '-'
               + new Date().toISOString().slice(0, 10) + '.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  // Credit color helper
  const creditColor = (remaining, total) => {
    if (remaining <= 0) return '#ef4444';
    if (remaining <= Math.ceil(total * 0.2)) return '#f59e0b';
    return '#22c55e';
  };

  // SoWs grouped by client for dropdowns
  const sowsByClient = sows.reduce((acc, s) => {
    const name = s.client_name || 'Unknown';
    if (!acc[name]) acc[name] = [];
    acc[name].push(s);
    return acc;
  }, {});

  // SoWs an engagement may be newly put on: this client's active ones. A
  // completed SoW should not collect new work.
  const getSowsForClient = (clientId) => sows.filter(s => s.client_id === clientId && s.status === 'Active');

  // What a cell must be able to SHOW is a different question. A <select> whose
  // value matches no option renders as its first option, so filtering to
  // Active made every engagement on a completed SoW read "— None —" here while
  // the database still held the assignment. Keep the current one in the list
  // whatever its status, so the cell reports what is true.
  const sowOptionsFor = (row) => {
    const options = getSowsForClient(row.client_id);
    if (!row.sow_id || options.some(s => String(s.id) === String(row.sow_id))) return options;
    const current = sows.find(s => String(s.id) === String(row.sow_id));
    return [...options, {
      id: row.sow_id,
      sow_number: current?.sow_number || row.sow_number || 'Assigned SoW',
      status: current?.status || '',
      inactive: true,
    }];
  };

  // Inline SoW dropdown cell component
  const SowCell = ({ row }) => {
    const clientActiveSows = sowOptionsFor(row);
    const isSaving = saving[row.id];

    return (
      <select
        value={row.sow_id || ''}
        disabled={isSaving || row.status === 'Cancelled'}
        onChange={e => assignSow(row.id, e.target.value, parseFloat(row.sow_credits) || 1)}
        onClick={e => e.stopPropagation()}
        style={{
          padding: '4px 6px', borderRadius: 'var(--radius)', fontSize: 12,
          border: '1px solid var(--border)', background: 'var(--bg-input)',
          color: row.sow_id ? 'var(--text)' : 'var(--text-secondary)',
          cursor: isSaving ? 'wait' : 'pointer', minWidth: 120, maxWidth: 180,
          opacity: isSaving ? 0.6 : 1
        }}>
        <option value="">— None —</option>
        {clientActiveSows.map(s => (
          <option key={s.id} value={s.id}
            disabled={s.inactive
              ? String(s.id) !== String(row.sow_id)
              : (s.remaining_credits <= 0 && s.id !== row.sow_id)}>
            {/* Credits remaining is the number that matters while a SoW is
                live; once it is closed its status is. */}
            {s.inactive
              ? `${s.sow_number}${s.status ? ` (${s.status})` : ''}`
              : `${s.sow_number} (${s.remaining_credits} left)`}
          </option>
        ))}
      </select>
    );
  };

  // Inline qty cell component
  // What the table shows, as functions, so the CSV export cannot drift from it.
  //
  // Amount and Service Order are both derived: an engagement may carry its own
  // value or inherit one from its statement of work. Recomputing that in two
  // places is how an exported spreadsheet comes to disagree with the screen it
  // came from, so the column renderers and the export both call these.

  /** Credits booked against the SoW. numeric(4,1) -- half credits are real. */
  const qtyFor = (row) => parseFloat(row.sow_credits) || 1;

  /** { value, derived } -- derived means calculated from the SoW, not recorded. */
  const amountFor = (row) => {
    const own = parseFloat(row.billing_amount);
    if (row.billing_amount && own > 0) return { value: own, derived: false };
    if (row.sow_id) {
      const sow = sows.find(s => s.id === row.sow_id);
      if (sow && parseFloat(sow.billing_amount) > 0 && sow.total_credits > 0) {
        const perCredit = parseFloat(sow.billing_amount) / sow.total_credits;
        return { value: perCredit * qtyFor(row), derived: true };
      }
    }
    return { value: null, derived: false };
  };

  /** { value, derived, sow } -- derived means it still lives only on the SoW. */
  const serviceOrderFor = (row) => {
    if (row.service_order) return { value: row.service_order, derived: false, sow: null };
    const sow = row.sow_id ? sows.find(s => s.id === row.sow_id) : null;
    if (sow && sow.service_order) return { value: sow.service_order, derived: true, sow };
    return { value: null, derived: false, sow: null };
  };

  const QtyCell = ({ row }) => {
    const isSaving = saving[row.id];

    return (
      <select
        value={parseFloat(row.sow_credits) || 1}
        disabled={isSaving || row.status === 'Cancelled' || !row.sow_id}
        onChange={e => assignSow(row.id, row.sow_id, parseFloat(e.target.value))}
        onClick={e => e.stopPropagation()}
        style={{
          width: 60, padding: '4px 6px', borderRadius: 'var(--radius)', fontSize: 12,
          border: '1px solid var(--border)', background: 'var(--bg-input)',
          color: 'var(--text)', textAlign: 'center', cursor: (!row.sow_id || isSaving) ? 'not-allowed' : 'pointer',
          opacity: (!row.sow_id || isSaving) ? 0.5 : 1
        }}>
        {[0.5,1,1.5,2,2.5,3,3.5,4,4.5,5].map(n => <option key={n} value={n}>{n}</option>)}
      </select>
    );
  };

  // Engagement columns
  const engColumns = [
    { key: 'client_name', label: 'Client' },
    { key: 'name', label: 'Name', render: (v, row) => v || <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{row.engagement_type}</span> },
    { key: 'status', label: 'Status', render: v => <Badge>{v}</Badge> },
    { key: 'sow_number', label: (
      <select value={sowFilter} onChange={e => { e.stopPropagation(); setSowFilter(e.target.value); }}
        style={{
          padding: '3px 6px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
          background: 'var(--bg-input)', color: sowFilter ? 'var(--primary)' : 'var(--text-secondary)',
          fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'uppercase',
          letterSpacing: '0.05em', width: '100%'
        }}>
        <option value="">SoW (All)</option>
        <option value="__none">— No SoW —</option>
        {Object.entries(sowsByClient).sort(([a],[b]) => a.localeCompare(b)).map(([client, clientSowList]) => (
          <optgroup key={client} label={client}>
            {clientSowList.map(s => <option key={s.id} value={s.id}>{s.sow_number}</option>)}
          </optgroup>
        ))}
      </select>
    ), render: (v, row) => <SowCell row={row} /> },
    { key: 'sow_credits', label: 'Qty', render: (v, row) => <QtyCell row={row} /> },
    { key: 'service_order', label: 'Service Order', render: (v, row) => {
      // The engagement gets its own copy only when the SoW is marked Complete,
      // so a drafted SoW's number showed here as nothing at all even though the
      // SoW already carried it. An inherited value appears in the same muted
      // italic the Amount column uses for a figure derived from the SoW rather
      // than recorded against the engagement.
      const so = serviceOrderFor(row);
      if (!so.value) return '—';
      if (!so.derived) return so.value;
      return <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}
                   title={`From SoW ${so.sow.sow_number} — copied onto the engagement when that SoW is marked Complete`}>
        {so.value}
      </span>;
    } },
    { key: 'billing_amount', label: 'Amount', render: (v, row) => {
      const amt = amountFor(row);
      if (amt.value === null) return '—';
      const money = `$${amt.value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
      if (!amt.derived) return money;
      return <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }} title="Calculated from SoW">{money}</span>;
    } },
    { key: 'is_billed', label: 'Billed', render: (v, r) => r.status === 'Cancelled' ? <Badge>Cancelled</Badge> : v ? <span style={{ color: '#22c55e' }}>✅ Billed</span> : <span style={{ color: '#f59e0b' }}>⏳ Pending</span> },
    { key: 'billed_date', label: 'Billed Date', render: v => v ? new Date(v).toLocaleDateString() : '—' },
    { key: 'id', label: 'Actions', render: (v, row) => row.status !== 'Cancelled' ? (
      <div style={{ display: 'flex', gap: 6 }}>
        {!row.is_billed && (
          <Button style={{ padding: '4px 12px', fontSize: 12 }}
            onClick={(e) => { e.stopPropagation(); markBilled(v); }}>
            Mark Billed
          </Button>
        )}
        <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
          onClick={(e) => { e.stopPropagation(); openBillModal(row); }}>
          Edit
        </Button>
      </div>
    ) : null }
  ];

  // SoW columns
  const sowColumns = [
    { key: 'client_name', label: 'Client' },
    { key: 'sow_number', label: 'SoW Number', render: (v, row) => <span style={{ fontWeight: 600 }}>{v} {row.original_name ? '📎' : ''}</span> },
    { key: 'purchase_order', label: 'PO Number', render: v => v || '—' },
    { key: 'description', label: 'Description', render: v => v ? <span style={{ fontSize: 13, maxWidth: 200, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</span> : '—' },
    { key: 'total_credits', label: 'Total Credits', render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'used_credits', label: 'Used', render: v => v || 0 },
    { key: 'remaining_credits', label: 'Remaining', render: (v, row) => (
      <span style={{ fontWeight: 700, color: creditColor(v, row.total_credits) }}>{v}</span>
    )},
    { key: 'billing_amount', label: 'Amount', render: v => v && parseFloat(v) > 0 ? `$${parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—' },
    { key: 'status', label: 'Status', render: v => <Badge>{v}</Badge> },
    { key: 'start_date', label: 'Start', render: v => v ? new Date(v).toLocaleDateString() : '—' },
    { key: 'end_date', label: 'End', render: v => v ? new Date(v).toLocaleDateString() : '—' },
    { key: 'id', label: 'Actions', render: (v, row) => (
      <div style={{ display: 'flex', gap: 6 }}>
        <Button variant="secondary" style={{ padding: '4px 10px', fontSize: 12 }}
          onClick={(e) => { e.stopPropagation(); openEditSow(row); }}>✏️ Edit</Button>
        <Button variant="danger" style={{ padding: '4px 10px', fontSize: 12 }}
          onClick={(e) => { e.stopPropagation(); deleteSow(v); }}>🗑️</Button>
      </div>
    )}
  ];

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 24 }}>Billing Management</h1>

      {/* Summary Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Total Billed" value={`$${parseFloat(summary.total_billed || 0).toLocaleString()}`} icon="💵" color="#22c55e" />
        <StatCard label="Unbilled" value={`$${parseFloat(summary.total_unbilled || 0).toLocaleString()}`} icon="⏳" color="#f59e0b" />
        <StatCard label="Billed Tests" value={summary.billed_count || 0} icon="✅" color="#3b82f6" />
        <StatCard label="Unbilled Tests" value={summary.unbilled_count || 0} icon="📋" color="#ef4444" />
        <StatCard label="Active SoWs" value={summary.active_sows || 0} icon="📑" color="#8b5cf6" />
        <StatCard label="Remaining Credits" value={summary.total_remaining_credits || 0} icon="🎟️" color="#06b6d4" />
      </div>

      {/* Tab Switcher */}
      <Card style={{ marginBottom: 16, display: 'flex', gap: 8, padding: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4, marginRight: 16 }}>
          {['engagements', 'sows'].map(tab => (
            <Button key={tab} variant={activeTab === tab ? 'primary' : 'secondary'}
              onClick={() => setActiveTab(tab)}
              style={{ fontWeight: activeTab === tab ? 600 : 400 }}>
              {tab === 'engagements' ? '📋 Engagements' : '📑 Statements of Work'}
            </Button>
          ))}
        </div>

        {/* Filters for engagements tab */}
        {activeTab === 'engagements' && (
          <>
            <div style={{ height: 24, borderLeft: '1px solid var(--border)', margin: '0 8px' }} />
            {['all', 'unbilled', 'billed', 'no_sow', 'cancelled'].map(f => (
              <Button key={f} variant={filter === f ? 'primary' : 'ghost'} onClick={() => setFilter(f)}
                style={{ textTransform: 'capitalize', padding: '6px 14px', fontSize: 13 }}>
                {f === 'all' ? 'All' : f === 'no_sow' ? 'No SoW' : f}
              </Button>
            ))}
            <Button variant="secondary" onClick={exportCsv} disabled={filtered.length === 0}
              title={filtered.length ? `Export these ${filtered.length} row(s) as CSV` : 'Nothing to export'}
              style={{ padding: '6px 14px', fontSize: 13 }}>
              Export CSV ({filtered.length})
            </Button>
            <div style={{ height: 24, borderLeft: '1px solid var(--border)', margin: '0 8px' }} />
            <select value={engClientFilter} onChange={e => setEngClientFilter(e.target.value)}
              style={{
                padding: '6px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
                background: 'var(--bg-input)', color: engClientFilter ? 'var(--primary)' : 'var(--text)', fontSize: 13
              }}>
              <option value="">All Clients</option>
              {[...new Set(engagements.map(e => JSON.stringify({ id: e.client_id, name: e.client_name })))].map(s => JSON.parse(s)).sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <input
              type="text" placeholder="Search engagement name..." value={engNameFilter}
              onChange={e => setEngNameFilter(e.target.value)}
              style={{
                padding: '6px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
                background: 'var(--bg-input)', color: 'var(--text)', fontSize: 13, minWidth: 180,
              }} />
          </>
        )}

        {/* Client filter + New SoW button on SoW tab */}
        {activeTab === 'sows' && (
          <>
            <div style={{ height: 24, borderLeft: '1px solid var(--border)', margin: '0 8px' }} />
            <select value={sowClientFilter} onChange={e => setSowClientFilter(e.target.value)}
              style={{
                padding: '6px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
                background: 'var(--bg-input)', color: sowClientFilter ? 'var(--primary)' : 'var(--text)', fontSize: 13
              }}>
              <option value="">All Clients</option>
              {[...new Set(sows.map(s => s.client_name))].filter(Boolean).sort().map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            <div style={{ flex: 1 }} />
            <Button variant="secondary" onClick={() => setShowSowWizard(true)}>Generate SoW</Button>
            <Button onClick={openNewSow}>+ New SoW</Button>
          </>
        )}
      </Card>

      {/* Engagements Tab */}
      {activeTab === 'engagements' && <Table columns={engColumns} data={filtered} />}

      {/* SoWs Tab */}
      {activeTab === 'sows' && <Table columns={sowColumns} data={sowClientFilter ? sows.filter(s => s.client_name === sowClientFilter) : sows} />}

      {/* Billing Update Modal */}
      <Modal isOpen={showBillModal} onClose={() => { setShowBillModal(false); setClientSows([]); }} title="Update Billing" width={480}>
        <Input label="Service Order" value={billForm.serviceOrder || ''}
          onChange={e => setBillForm(f => ({ ...f, serviceOrder: e.target.value }))} placeholder="e.g. SO-2026-001" />
        <Input label="Billing Amount ($)" type="number" min="0" step="0.01"
          value={billForm.billingAmount || ''} onChange={e => setBillForm(f => ({ ...f, billingAmount: e.target.value }))} />

        {/* SoW Assignment */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>
            Statement of Work
          </label>
          <select value={billForm.sowId || ''} onChange={e => {
              const sowId = e.target.value;
              const sow = clientSows.find(s => s.id === sowId);
              const credits = parseFloat(billForm.sowCredits) || 1;
              const calcAmt = sow && parseFloat(sow.billing_amount) > 0 && sow.total_credits > 0
                ? ((parseFloat(sow.billing_amount) / sow.total_credits) * credits).toFixed(2) : '';
              setBillForm(f => ({ ...f, sowId, billingAmount: calcAmt || f.billingAmount }));
            }}
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
              background: 'var(--bg-input)', color: 'var(--text)', fontSize: 14
            }}>
            <option value="">— No SoW —</option>
            {clientSows.map(s => (
              <option key={s.id} value={s.id}
                disabled={s.assignable === false
                  ? s.id !== billForm.sowId
                  : (s.remaining_credits <= 0 && s.id !== billForm.sowId)}>
                {s.assignable === false
                  ? `${s.sow_number} (${s.status})`
                  : `${s.sow_number} (${s.remaining_credits} credit${s.remaining_credits !== 1 ? 's' : ''} remaining)`}
              </option>
            ))}
          </select>
          {clientSows.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
              No active SoWs for this client
            </div>
          )}
        </div>

        {/* Credits Quantity — only shown when a SoW is selected */}
        {billForm.sowId && (
          <>
            <Select label="Credits (Qty)" value={billForm.sowCredits || 1}
              onChange={e => {
                const credits = parseFloat(e.target.value);
                const sow = clientSows.find(s => s.id === billForm.sowId);
                const calcAmt = sow && parseFloat(sow.billing_amount) > 0 && sow.total_credits > 0
                  ? ((parseFloat(sow.billing_amount) / sow.total_credits) * credits).toFixed(2) : '';
                setBillForm(f => ({ ...f, sowCredits: e.target.value, billingAmount: calcAmt || f.billingAmount }));
              }}
              options={[0.5,1,1.5,2,2.5,3,3.5,4,4.5,5].map(n => ({ value: n, label: `${n}` }))} />
            {(() => {
              const sow = clientSows.find(s => s.id === billForm.sowId);
              if (sow && parseFloat(sow.billing_amount) > 0 && sow.total_credits > 0) {
                const perCredit = parseFloat(sow.billing_amount) / sow.total_credits;
                return <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -10, marginBottom: 12 }}>
                  ${perCredit.toFixed(2)} per credit × {billForm.sowCredits || 1} = ${(perCredit * (parseFloat(billForm.sowCredits) || 1)).toFixed(2)}
                </div>;
              }
              return null;
            })()}
          </>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <input type="checkbox" checked={billForm.isBilled || false} onChange={e => setBillForm(f => ({ ...f, isBilled: e.target.checked }))} />
          <span style={{ fontSize: 14 }}>Mark as Billed</span>
        </label>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => { setShowBillModal(false); setClientSows([]); }}>Cancel</Button>
          <Button onClick={saveBilling}>Save</Button>
        </div>
      </Modal>

      {/* SoW Create/Edit Modal */}
      <Modal isOpen={showSowModal} onClose={() => { setShowSowModal(false); setEditingSow(null); }} title={editingSow ? 'Edit Statement of Work' : 'New Statement of Work'} width={560}>
        {!editingSow && (
          <Select label="Client" value={sowForm.clientId || ''}
            onChange={e => setSowForm(f => ({ ...f, clientId: e.target.value }))}
            options={[{ value: '', label: '— Select Client —' }, ...clients.map(c => ({ value: c.id, label: c.name }))]} />
        )}
        <Input label="SoW Number" value={sowForm.sowNumber || ''} placeholder="e.g. SOW-2026-001"
          onChange={e => setSowForm(f => ({ ...f, sowNumber: e.target.value }))} />
        <Input label="Purchase Order" value={sowForm.purchaseOrder || ''} placeholder="The client's PO number, if they have raised one"
          onChange={e => setSowForm(f => ({ ...f, purchaseOrder: e.target.value }))} />
        <Input label="Service Order" value={sowForm.serviceOrder || ''} placeholder="e.g. SO-2026-001"
          onChange={e => setSowForm(f => ({ ...f, serviceOrder: e.target.value }))} />
        <Input label="Description" value={sowForm.description || ''} placeholder="Brief description"
          onChange={e => setSowForm(f => ({ ...f, description: e.target.value }))} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Input label="Total Credits" type="number" min="1" value={sowForm.totalCredits || ''}
            onChange={e => setSowForm(f => ({ ...f, totalCredits: e.target.value }))} />
          <Input label="Billing Amount ($)" type="number" min="0" step="0.01" value={sowForm.billingAmount || ''}
            onChange={e => setSowForm(f => ({ ...f, billingAmount: e.target.value }))} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Input label="Start Date" type="date" value={sowForm.startDate || ''}
            onChange={e => setSowForm(f => ({ ...f, startDate: e.target.value }))} />
          <Input label="End Date" type="date" value={sowForm.endDate || ''}
            onChange={e => setSowForm(f => ({ ...f, endDate: e.target.value }))} />
        </div>
        <Select label="Status" value={sowForm.status || 'Active'}
          onChange={e => setSowForm(f => ({ ...f, status: e.target.value }))}
          options={[
            { value: 'Draft', label: 'Draft' },
            { value: 'Waiting on Client', label: 'Waiting on Client' },
            { value: 'Active', label: 'Active' },
            { value: 'Complete', label: 'Complete' },
            { value: 'Cancelled', label: 'Cancelled' }
          ]} />
        <TextArea label="Notes" value={sowForm.notes || ''} placeholder="Internal notes..."
          onChange={e => setSowForm(f => ({ ...f, notes: e.target.value }))} />

        {/* SoW Document Upload */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>
            SoW Document
          </label>
          {sowForm.existingFile && !sowFile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--bg-input)', borderRadius: 'var(--radius)', marginBottom: 8, fontSize: 13 }}>
              <span>📄</span>
              <span style={{ flex: 1, fontWeight: 500 }}>{sowForm.existingFile}</span>
              <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                {sowForm.fileSize ? `${(sowForm.fileSize / 1024).toFixed(0)} KB` : ''}
              </span>
              <Button variant="secondary" style={{ padding: '2px 8px', fontSize: 11 }}
                onClick={() => downloadSowFile(editingSow, sowForm.existingFile)}>Download</Button>
              <Button variant="danger" style={{ padding: '2px 8px', fontSize: 11 }}
                onClick={() => removeSowFile(editingSow)}>Remove</Button>
            </div>
          )}
          <input type="file" onChange={e => setSowFile(e.target.files[0])}
            style={{ fontSize: 13, color: 'var(--text)' }} />
          {sowFile && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
              Selected: {sowFile.name} ({(sowFile.size / 1024).toFixed(0)} KB)
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => { setShowSowModal(false); setEditingSow(null); setSowFile(null); }}>Cancel</Button>
          <Button onClick={saveSow} disabled={!editingSow && (!sowForm.clientId || !sowForm.sowNumber)}>
            {editingSow ? 'Update SoW' : 'Create SoW'}
          </Button>
        </div>
      </Modal>

      {/* SoW Generation Wizard */}
      <SowWizard isOpen={showSowWizard} onClose={() => setShowSowWizard(false)} onGenerated={() => load()} />
    </div>
  );
}
